import { DatePicker } from '@/components/basic';
import { SelectMenuSingle } from '@/components/basic/SelectMenu/SelectMenuSingle';
import { SelectOffices } from '@/components/partial/SelectOffices';
import { AdminVisitsFilterState } from '@/hooks/useAdminVisitsTable';
import { useI18n } from '@/hooks/useI18n';
import { cn } from '@/lib/utils';
import { useAuthenticated } from '@gettactic/hooks';
import { CalendarIcon } from '@heroicons/react/outline';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { HiX } from 'react-icons/hi';

type Props = {
  updateFilter: React.Dispatch<Partial<AdminVisitsFilterState>>;
  filter: AdminVisitsFilterState;
  type: 'invitations' | 'visits';
  defaultOfficeId?: string | null;
  isRedesign?: boolean;
};

export function VisitsFilters({ filter, updateFilter, type, isRedesign, defaultOfficeId }: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);

  const now = useMemo(() => new Date(), []);

  // We are going to work with a local state for the dates
  // this way we only update the filter when the user is done selecting both dates
  const [dates, setDates] = useState({ start: filter.start, end: filter.end });
  useEffect(() => {
    if (dates.start !== filter.start || dates.end !== filter.end) {
      setDates({ start: filter.start, end: filter.end });
    }
  }, [filter.start, filter.end]);

  const onChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setDates({ start, end });
    if (start && end) {
      updateFilter({ start, end });
    }
  };

  const displayDates = useMemo(() => {
    if (filter.start && filter.end) {
      return `${format(filter.start, templates.displayDate)} - ${format(filter.end, templates.displayDate)}`;
    }
    return 'Select Dates';
  }, [filter.start, filter.end, templates.displayDate]);

  return (
    <div className={cn("inline-flex flex-wrap items-center  gap-4", 
    isRedesign ? "" : "ml-2 my-8")}>
      <div className="w-62 flex items-center">
        <DatePicker
          allowSameDay={false}
          selected={dates.start}
          minDate={type === 'invitations' ? now : undefined}
          onChange={onChange as () => void}
          startDate={dates.start}
          endDate={dates.end}
          //@ts-expect-error bad typing lib
          selectsRange
          calendarClassName="tailwind-datepicker"
          className="input text-tertiary  w-60 rounded-lg  "
          customInput={
            <button
              type="button"
              className={cn('inline-flex items-center space-x-2 rounded-full border-transparent bg-white text-gray-800 focus:outline-none',
                isRedesign ? "border-none focus:border-none focus:ring-none" : 'border border-gray-300 p-2 focus:ring-2 focus:ring-primary focus:ring-offset-2'
              )}
            >
              <CalendarIcon className="h-6 w-6" />
              <span>{displayDates}</span>
            </button>
          }
        />
        {filter.start && filter.end ? (
          <button
            title="Clear dates"
            className="mx-1"
            onClick={() => updateFilter({ start: null, end: null })}
          >
            <HiX className="h-4 w-4 text-gray-600" />
          </button>
        ) : null}
      </div>

      {!isRedesign && <SelectOffices
        className="w-[12rem]"
        onChange={(office) => updateFilter({ office })}
        offices={filter.office}
      />}

      {type === 'visits' ? (
        <>
          <SelectMenuSingle
            value={
              filter.uninvited
                ? 'uninvited'
                : filter.invited
                  ? 'invited'
                  : 'all'
            }
            setValue={(value) => {
              if (value === 'all') {
                updateFilter({ invited: false, uninvited: false });
              } else if (value === 'invited') {
                updateFilter({ invited: true, uninvited: false });
              } else if (value === 'uninvited') {
                updateFilter({ invited: false, uninvited: true });
              }
            }}
            options={[
              { label: 'All Types', value: 'all' },
              { label: 'Invited', value: 'invited' },
              { label: 'Walk-ins', value: 'uninvited' }
            ]}
          />
        </>
      ) : null}
    </div>
  );
}
